import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { Badge } from '@components';
import { TenantProfileDocumentStatus } from '@libs/constants';
import { useTrans } from '@libs/i18n';
import { EyeIcon, TrashIcon } from '@phosphor-icons/react';

import {
  useDeleteProfileDocument,
  useListProfileDocuments,
} from '../../api/tenant-profile-documents-api';
import type { TenantProfileDocumentDto } from '../../api/types';
import { ORG_TOKENS } from '../utils/tokens';

const CLASSIFYING = TenantProfileDocumentStatus.CLASSIFYING;
const EXTRACTING = TenantProfileDocumentStatus.EXTRACTING;
const READY_TO_EXTRACT = TenantProfileDocumentStatus.READY_TO_EXTRACT;
const PENDING_REVIEW = TenantProfileDocumentStatus.PENDING_REVIEW;
const INVALID = TenantProfileDocumentStatus.INVALID;

/**
 * Shown while documents are classifying, ready to extract, extracting,
 * extracted-but-not-yet-reviewed (PENDING_REVIEW), or classified as irrelevant
 * (INVALID). Extracted docs stay listed with a "New" badge + "Ready to review"
 * pill so they don't vanish the moment extraction finishes; clicking an
 * extracted row opens its review page directly. Irrelevant docs stay listed
 * with a Remove button so the user can delete them from here. The bar only
 * disappears once every document is reviewed (DONE) or removed. For EXTRACTING
 * docs it shows a linear progress bar.
 */
export const InFlightDocumentsBar: FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { data: docs = [] } = useListProfileDocuments();

  const { inFlight, total, extracting, pct } = useMemo(() => {
    const inFlightDocs = docs.filter(
      (d) =>
        d.status === CLASSIFYING ||
        d.status === EXTRACTING ||
        d.status === READY_TO_EXTRACT ||
        d.status === PENDING_REVIEW ||
        d.status === INVALID,
    );
    const extractingCount = inFlightDocs.filter((d) => d.status === EXTRACTING).length;
    const pendingCount = inFlightDocs.filter((d) => d.status === PENDING_REVIEW).length;
    const tot = extractingCount + pendingCount;
    // Only the 3 most-recently uploaded docs render here; the rest live in the
    // document hub (reachable via the "View all documents" button). ISO
    // `uploadedAt` strings sort chronologically as plain strings.
    const latest = [...inFlightDocs]
      .sort((a, b) => (b.uploadedAt ?? '').localeCompare(a.uploadedAt ?? ''))
      .slice(0, 3);
    return {
      inFlight: latest,
      total: inFlightDocs.length,
      extracting: extractingCount,
      pct: tot === 0 ? 0 : Math.round((pendingCount / tot) * 100),
    };
  }, [docs]);

  if (total === 0) return null;

  const showProgress = extracting > 0;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.300',
        borderRadius: `${ORG_TOKENS.radiusMd}px`,
        backgroundColor: 'background.paper',
        overflow: 'hidden',
      }}
    >
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: 'grey.200',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="textSM_semiBold" color="text.primary">
              {showProgress
                ? t('app.orgProfile.inFlight.extractionInProgress')
                : t('app.orgProfile.inFlight.title', { count: total })}
            </Typography>
          </Box>
          <Button
            size="small"
            variant="tertiary"
            onClick={() => navigate('/tenant-profile/document-hub')}
            startIcon={<EyeIcon size={16} />}
            sx={{ textTransform: 'none', color: 'text.secondary', flex: 'none' }}
          >
            {t('app.orgProfile.inFlight.viewAll')}
          </Button>
        </Box>

        {/* Per-document rows */}
        {inFlight.map((doc) => (
          <InFlightRow key={doc.id} doc={doc} />
        ))}

        {/* Extraction progress bar */}
        {showProgress && (
          <Box sx={{ px: 2, pt: 0.75, pb: 1.25 }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'blue.100',
                '& .MuiLinearProgress-bar': { borderRadius: 3, backgroundColor: 'blue.500' },
              }}
            />
          </Box>
        )}
    </Box>
  );
});

const InFlightRow: FC<{ doc: TenantProfileDocumentDto }> = observer(({ doc }) => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const removeDoc = useDeleteProfileDocument();
  const status = doc.status;
  const isReady = status === PENDING_REVIEW;
  const isInvalid = status === INVALID;

  const dotColor =
    status === EXTRACTING ? 'blue.500' :
    status === PENDING_REVIEW ? 'green.500' :
    status === READY_TO_EXTRACT ? 'warning.main' :
    status === INVALID ? 'yellow.700' :
    'grey.400';

  const statusLabel =
    status === EXTRACTING ? t('app.orgProfile.inFlight.extracting') :
    status === PENDING_REVIEW ? t('app.orgProfile.inFlight.extracted') :
    status === READY_TO_EXTRACT ? t('app.orgProfile.inFlight.readyToExtract') :
    status === INVALID ? t('app.orgProfile.inFlight.irrelevant') :
    t('app.orgProfile.inFlight.classifying');

  const statusColor =
    status === EXTRACTING ? 'blue.700' :
    status === PENDING_REVIEW ? 'green.700' :
    status === READY_TO_EXTRACT ? 'yellow.800' :
    'text.tertiary';

  // Extracted docs open their review page on click — same destination as the
  // "Review" action in the document hub.
  const openReview = () => navigate(`/tenant-profile/document-hub/${doc.id}/review`);

  // Irrelevant docs are removed (soft-deleted) from here — not re-classified.
  const handleRemove = () => {
    if (removeDoc.isPending) return;
    removeDoc.mutate(doc.id, {
      onError: () =>
        enqueueSnackbar(t('app.orgProfile.inFlight.removeFailed'), { variant: 'error' }),
    });
  };

  return (
    <Box
      onClick={isReady ? openReview : undefined}
      role={isReady ? 'button' : undefined}
      tabIndex={isReady ? 0 : undefined}
      onKeyDown={
        isReady
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openReview();
              }
            }
          : undefined
      }
      title={isReady ? t('app.orgProfile.inFlight.reviewHint') : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0.875,
        borderBottom: 1,
        borderColor: 'grey.100',
        '&:last-child': { borderBottom: 'none' },
        ...(isReady && {
          cursor: 'pointer',
          transition: 'background-color 0.15s',
          '&:hover': { backgroundColor: 'green.100' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'green.500', outlineOffset: -2 },
        }),
      }}
    >
      {/* Status dot */}
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          flex: 'none',
          backgroundColor: dotColor,
        }}
      />

      {/* Filename + New badge */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          variant="textSM_regular"
          color="text.primary"
          noWrap
          sx={{ minWidth: 0 }}
        >
          {doc.fileName}
        </Typography>
        {isReady && (
          <Badge
            label={t('app.orgProfile.inFlight.new')}
            variant="Processing"
            sx={{ height: 18, textTransform: 'uppercase', letterSpacing: '0.02em', '& .MuiChip-label': { px: 0.75, fontSize: '0.625rem', fontWeight: 700 } }}
          />
        )}
      </Box>

      {/* Status label — extracted docs get a green pill; irrelevant docs get an
          amber badge whose trailing (delete) icon removes the document. */}
      {isReady ? (
        <Badge
          label={statusLabel}
          variant="Completed"
          icon={<EyeIcon size={16} weight="bold" />}
          sx={{ flex: 'none' }}
        />
      ) : isInvalid ? (
        <Badge
          label={statusLabel}
          variant="Submitted"
          sx={{
            flex: 'none',
            '& .MuiChip-deleteIcon': {
              color: 'inherit',
              opacity: 0.9,
              '&:hover': { opacity: 1, color: 'inherit' },
            },
          }}
          chipProps={{
            'aria-label': t('app.orgProfile.inFlight.remove'),
            onDelete: handleRemove,
            deleteIcon: removeDoc.isPending ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <TrashIcon size={14} />
            ),
          }}
        />
      ) : (
        <Typography variant="textXS_semiBold" color={statusColor} sx={{ flex: 'none', whiteSpace: 'nowrap' }}>
          {statusLabel}
        </Typography>
      )}
    </Box>
  );
});
